import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Bot, Edit2, Image as ImageIcon, MapPinned, Save, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';

function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || `npc-${Date.now()}`;
}

function parseJsonText(value) {
  if (!value) return {};
  try {
    return JSON.parse(value);
  } catch (_) {
    return { raw: String(value) };
  }
}

function resolveImageSrc(image) {
  if (!image) return '';
  if (/^https?:\/\//i.test(image) || image.startsWith('/')) return image;
  const apiBaseUrl = import.meta.env.VITE_API_URL || '';
  return `${apiBaseUrl}/uploads/gallery/${image}`;
}

function parseObjectJson(value, fallback = {}) {
  if (!value) return fallback;
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : fallback;
  } catch (_) {
    return fallback;
  }
}

function getPreviewFrames(animationMap, behavior) {
  if (Array.isArray(animationMap?.[behavior]) && animationMap[behavior].length) {
    return animationMap[behavior];
  }
  if (behavior !== 'idle' && Array.isArray(animationMap?.idle) && animationMap.idle.length) {
    return animationMap.idle;
  }
  return [1];
}

function loadImageSize(imageSrc) {
  return new Promise((resolve) => {
    if (!imageSrc) {
      resolve({ width: 0, height: 0 });
      return;
    }

    const img = new window.Image();
    img.onload = () => resolve({ width: img.naturalWidth || 0, height: img.naturalHeight || 0 });
    img.onerror = () => resolve({ width: 0, height: 0 });
    img.src = imageSrc;
  });
}

function getPositiveDivisors(value, limit = 24) {
  const size = Math.max(0, Number(value) || 0);
  if (!size) return [];
  const divisors = [];
  for (let index = 1; index <= Math.min(limit, size); index += 1) {
    if (size % index === 0) {
      divisors.push(index);
    }
  }
  return divisors;
}

const CHARSET_PRESETS = {
  auto: {
    label: 'Auto',
    columns: null,
    rows: null,
    mode: 'auto'
  },
  rpg_maker: {
    label: 'RPG Maker clásico',
    columns: 3,
    rows: 4,
    mode: 'preset'
  },
  classic_3x4: {
    label: '3x4 estándar',
    columns: 3,
    rows: 4,
    mode: 'preset'
  },
  npc_4x4: {
    label: '4x4 NPC',
    columns: 4,
    rows: 4,
    mode: 'preset'
  },
  free: {
    label: 'Sheet libre',
    columns: 1,
    rows: 1,
    mode: 'free'
  }
};

function detectBestActorLayout(imageInfo, kind = 'actor') {
  const width = Math.max(0, Number(imageInfo?.width) || 0);
  const height = Math.max(0, Number(imageInfo?.height) || 0);
  if (!width || !height) return null;

  const targetWidth = kind === 'player' ? 32 : 32;
  const targetHeight = kind === 'player' ? 48 : 32;
  const preferredColumns = kind === 'player' ? [3, 4, 6, 8, 12] : [4, 3, 8, 6, 12];
  const preferredRows = [4, 8, 3, 2];
  const widthDivisors = getPositiveDivisors(width, 24);
  const heightDivisors = getPositiveDivisors(height, 24);
  const commonFrameSizes = new Set([16, 24, 32, 48, 64, 96]);

  const candidates = [];
  widthDivisors.forEach((columns) => {
    heightDivisors.forEach((rows) => {
      const frameWidth = Math.max(1, Math.floor(width / columns));
      const frameHeight = Math.max(1, Math.floor(height / rows));
      const frameCount = columns * rows;
      let score = 1000;

      score -= Math.abs(frameWidth - targetWidth) * 8;
      score -= Math.abs(frameHeight - targetHeight) * 8;

      if (preferredColumns.includes(columns)) {
        score += 40;
      }
      if (preferredRows.includes(rows)) {
        score += 30;
      }
      if (rows === 4) score += 40;
      if (rows === 8) score += 24;
      if (columns === 3) score += 38;
      if (columns === 4) score += 36;
      if (columns === 8) score += 18;
      if (columns === 12) score += 14;

      if (frameWidth % 8 === 0) score += 24;
      if (frameHeight % 8 === 0) score += 24;
      if (frameWidth % 16 === 0) score += 10;
      if (frameHeight % 16 === 0) score += 10;

      if (commonFrameSizes.has(frameWidth)) score += 20;
      if (commonFrameSizes.has(frameHeight)) score += 20;

      if (frameCount >= 12) score += 10;
      if (frameCount >= 24) score += 8;
      if (frameCount >= 48) score += 6;
      if (frameCount >= 96) score += 4;

      if (frameWidth < 12 || frameHeight < 12) score -= 60;
      if (frameWidth > 128 || frameHeight > 128) score -= 80;

      candidates.push({
        columns,
        rows,
        frameWidth,
        frameHeight,
        score
      });
    });
  });

  candidates.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    const aDistance = Math.abs(a.frameWidth - targetWidth) + Math.abs(a.frameHeight - targetHeight);
    const bDistance = Math.abs(b.frameWidth - targetWidth) + Math.abs(b.frameHeight - targetHeight);
    if (aDistance !== bDistance) return aDistance - bDistance;
    const aShapeBias = Math.abs(a.columns - (kind === 'player' ? 3 : 4)) + Math.abs(a.rows - 4);
    const bShapeBias = Math.abs(b.columns - (kind === 'player' ? 3 : 4)) + Math.abs(b.rows - 4);
    return aShapeBias - bShapeBias;
  });

  return candidates[0] || null;
}

function normalizeTemplateForm(form) {
  const metadata = parseJsonText(form.metadata);
  metadata.show_foot_shadow = form.show_foot_shadow !== false;
  metadata.use_player_scale = form.use_player_scale !== false;
  if (form.use_player_scale === false) {
    metadata.scale = Math.max(0.5, Number(form.scale) || 1.65);
  } else {
    delete metadata.scale;
  }
  return {
    id: form.id || null,
    slug: String(form.slug || slugify(form.name)).trim(),
    name: String(form.name || '').trim(),
    category: String(form.category || 'civilian').trim() || 'civilian',
    sprite_image: String(form.sprite_image || '').trim() || null,
    frame_width: Number(form.frame_width) || 32,
    frame_height: Number(form.frame_height) || 32,
    columns_count: Math.max(1, Number(form.columns_count) || 4),
    rows_count: Math.max(1, Number(form.rows_count) || 4),
    direction_map: parseJsonText(form.direction_map),
    animation_map: parseJsonText(form.animation_map),
    personality: String(form.personality || '').trim(),
    ai_enabled: !!form.ai_enabled,
    metadata,
    scale: form.use_player_scale === false ? Math.max(0.5, Number(form.scale) || 1.65) : null,
    use_player_scale: form.use_player_scale !== false,
    show_foot_shadow: form.show_foot_shadow !== false,
    is_active: !!form.is_active
  };
}

function normalizeInstanceForm(form) {
  return {
    id: form.id || null,
    city_id: Number(form.city_id) || 0,
    template_id: form.template_id ? Number(form.template_id) : null,
    name: String(form.name || '').trim(),
    x: Number(form.x) || 0,
    y: Number(form.y) || 0,
    layer: Number(form.layer) || 1,
    facing: String(form.facing || 'south').trim() || 'south',
    behavior: String(form.behavior || 'idle').trim() || 'idle',
    dialog_text: String(form.dialog_text || '').trim(),
    target: String(form.target || '').trim(),
    is_ai_enabled: !!form.is_ai_enabled,
    is_enabled: !!form.is_enabled,
    metadata: parseJsonText(form.metadata)
  };
}

function emptyTemplateForm() {
  return {
    id: null,
    slug: '',
    name: '',
    category: 'civilian',
    sprite_image: '',
    frame_width: 32,
    frame_height: 32,
    columns_count: 4,
    rows_count: 4,
    direction_map: '{"south":0,"west":1,"east":2,"north":3}',
    animation_map: '{"idle":[0],"walk":[0,1,2,1]}',
    personality: '',
    ai_enabled: false,
    scale: 1.65,
    use_player_scale: true,
    metadata: '{}',
    show_foot_shadow: true,
    is_active: true
  };
}

function emptyInstanceForm(cityId = '') {
  return {
    id: null,
    city_id: cityId ? String(cityId) : '',
    template_id: '',
    name: '',
    x: 0,
    y: 0,
    layer: 1,
    facing: 'south',
    behavior: 'idle',
    dialog_text: '',
    target: '',
    is_ai_enabled: false,
    is_enabled: true,
    metadata: '{}'
  };
}

function normalizePlayerAppearanceForm(form) {
  return {
    sprite_image: String(form.sprite_image || '').trim() || null,
    frame_width: Number(form.frame_width) || 32,
    frame_height: Number(form.frame_height) || 48,
    columns_count: Math.max(1, Number(form.columns_count) || 3),
    rows_count: Math.max(1, Number(form.rows_count) || 4),
    direction_map: parseJsonText(form.direction_map),
    animation_map: parseJsonText(form.animation_map),
    scale: Math.max(0.5, Number(form.scale) || 1.65),
    show_foot_shadow: form.show_foot_shadow !== false
  };
}

function emptyPlayerAppearanceForm() {
  return {
    sprite_image: '',
    frame_width: 32,
    frame_height: 48,
    columns_count: 3,
    rows_count: 4,
    direction_map: '{"south":0,"west":1,"east":2,"north":3}',
    animation_map: '{"idle":[1],"walk":[0,1,2,1]}',
    scale: 1.65,
    show_foot_shadow: true
  };
}

function RpgNpcManager({
  templates = [],
  instances = [],
  cities = [],
  playerAppearance = null,
  onSaveTemplate,
  onDeleteTemplate,
  onSaveInstance,
  onDeleteInstance,
  onSavePlayerAppearance,
  onOpenImageGallery,
  isSavingTemplate = false,
  isSavingInstance = false,
  isSavingPlayerAppearance = false
}, ref) {
  const [activeSection, setActiveSection] = useState('templates');
  const [templateForm, setTemplateForm] = useState(emptyTemplateForm());
  const [instanceForm, setInstanceForm] = useState(emptyInstanceForm());
  const [playerForm, setPlayerForm] = useState(emptyPlayerAppearanceForm());
  const [selectedCityId, setSelectedCityId] = useState('');
  const [templatePreviewFacing, setTemplatePreviewFacing] = useState('south');
  const [templatePreviewBehavior, setTemplatePreviewBehavior] = useState('walk');
  const [templatePreviewScale, setTemplatePreviewScale] = useState(2.2);
  const [templatePreviewAutoplay, setTemplatePreviewAutoplay] = useState(true);
  const [templatePreviewTick, setTemplatePreviewTick] = useState(0);
  const [templateImageInfo, setTemplateImageInfo] = useState({ width: 0, height: 0 });
  const [templatePreset, setTemplatePreset] = useState('auto');
  const templateLayoutGuess = useMemo(() => detectBestActorLayout(templateImageInfo, 'npc'), [templateImageInfo]);
  const [playerPreviewFacing, setPlayerPreviewFacing] = useState('south');
  const [playerPreviewBehavior, setPlayerPreviewBehavior] = useState('walk');
  const [playerPreviewAutoplay, setPlayerPreviewAutoplay] = useState(true);
  const [playerPreviewTick, setPlayerPreviewTick] = useState(0);
  const [playerImageInfo, setPlayerImageInfo] = useState({ width: 0, height: 0 });
  const [playerPreset, setPlayerPreset] = useState('auto');
  const playerLayoutGuess = useMemo(() => detectBestActorLayout(playerImageInfo, 'player'), [playerImageInfo]);
  const playerScaleValue = Math.max(0.5, Number(playerForm.scale) || 1.65);
  const defaultNpcScale = Math.max(0.5, Number(playerAppearance?.scale) || playerScaleValue || 1.65);
  const normalizedCities = useMemo(() => {
    if (Array.isArray(cities)) return cities;
    if (Array.isArray(cities?.cities)) return cities.cities;
    if (Array.isArray(cities?.data)) return cities.data;
    return [];
  }, [cities]);

  useImperativeHandle(ref, () => ({
    setNpcSpriteImage: (image) => {
      setTemplateForm((current) => ({ ...current, sprite_image: image }));
    },
    setPlayerSpriteImage: (image) => {
      setPlayerForm((current) => ({ ...current, sprite_image: image }));
    }
  }), []);

  const sortedTemplates = useMemo(() => {
    return [...templates].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [templates]);

  const sortedCities = useMemo(() => {
    return [...normalizedCities].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [normalizedCities]);

  useEffect(() => {
    if (sortedCities.length === 0) {
      setSelectedCityId('');
      setInstanceForm(emptyInstanceForm());
      return;
    }

    const cityExists = sortedCities.some((city) => Number(city.id) === Number(selectedCityId));
    const nextCity = cityExists
      ? sortedCities.find((city) => Number(city.id) === Number(selectedCityId))
      : sortedCities[0];

    if (nextCity && !cityExists) {
      setSelectedCityId(String(nextCity.id));
    }

    if (nextCity && !instanceForm.id) {
      setInstanceForm((current) => ({
        ...current,
        city_id: String(nextCity.id)
      }));
    }
  }, [instanceForm.id, selectedCityId, sortedCities]);

  useEffect(() => {
    if (!playerAppearance) {
      setPlayerForm(emptyPlayerAppearanceForm());
      return;
    }
    setPlayerForm({
      sprite_image: playerAppearance.sprite_image || '',
      frame_width: Number(playerAppearance.frame_width) || 32,
      frame_height: Number(playerAppearance.frame_height) || 48,
      columns_count: Number(playerAppearance.columns_count) || 3,
      rows_count: Number(playerAppearance.rows_count) || 4,
      direction_map: JSON.stringify(playerAppearance.direction_map || { south: 0, west: 1, east: 2, north: 3 }, null, 2),
      animation_map: JSON.stringify(playerAppearance.animation_map || { idle: [1], walk: [0, 1, 2, 1] }, null, 2),
      scale: Number(playerAppearance.scale) || 1.65,
      show_foot_shadow: playerAppearance.show_foot_shadow !== false
    });
  }, [playerAppearance]);

  useEffect(() => {
    if (templateForm.id || templateForm.use_player_scale === false) return;
    setTemplateForm((current) => {
      const currentScale = Math.max(0.5, Number(current.scale) || 0);
      if (Math.abs(currentScale - defaultNpcScale) < 0.001) {
        return current;
      }
      return {
        ...current,
        scale: defaultNpcScale
      };
    });
  }, [defaultNpcScale, templateForm.id, templateForm.use_player_scale]);

  useEffect(() => {
    if (!templatePreviewAutoplay) return undefined;
    const timer = window.setInterval(() => {
      setTemplatePreviewTick((current) => current + 1);
    }, 220);
    return () => window.clearInterval(timer);
  }, [templatePreviewAutoplay]);

  useEffect(() => {
    if (!playerPreviewAutoplay) return undefined;
    const timer = window.setInterval(() => {
      setPlayerPreviewTick((current) => current + 1);
    }, 220);
    return () => window.clearInterval(timer);
  }, [playerPreviewAutoplay]);

  useEffect(() => {
    let cancelled = false;
    const imageSrc = resolveImageSrc(templateForm.sprite_image);
    if (!imageSrc) {
      setTemplateImageInfo({ width: 0, height: 0 });
      return undefined;
    }

    void loadImageSize(imageSrc).then((size) => {
      if (!cancelled) {
        setTemplateImageInfo(size);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [templateForm.sprite_image]);

  useEffect(() => {
    if (!templateForm.sprite_image || !templateImageInfo.width || !templateImageInfo.height) return;
    const defaults = emptyTemplateForm();
    const isDefaultLayout = Number(templateForm.columns_count) === defaults.columns_count && Number(templateForm.rows_count) === defaults.rows_count;
    if (!isDefaultLayout || templatePreset !== 'auto') return;
    const nextLayout = detectBestActorLayout(templateImageInfo, 'npc');
    if (!nextLayout) return;
    setTemplateForm((current) => {
      if (Number(current.columns_count) === nextLayout.columns && Number(current.rows_count) === nextLayout.rows) {
        return current;
      }
      return {
        ...current,
        columns_count: nextLayout.columns,
        rows_count: nextLayout.rows
      };
    });
  }, [templateForm.sprite_image, templateForm.columns_count, templateForm.rows_count, templateImageInfo.height, templateImageInfo.width, templatePreset]);

  useEffect(() => {
    let cancelled = false;
    const imageSrc = resolveImageSrc(playerForm.sprite_image);
    if (!imageSrc) {
      setPlayerImageInfo({ width: 0, height: 0 });
      return undefined;
    }

    void loadImageSize(imageSrc).then((size) => {
      if (!cancelled) {
        setPlayerImageInfo(size);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [playerForm.sprite_image]);

  useEffect(() => {
    if (!playerForm.sprite_image || !playerImageInfo.width || !playerImageInfo.height) return;
    const defaults = emptyPlayerAppearanceForm();
    const isDefaultLayout = Number(playerForm.columns_count) === defaults.columns_count && Number(playerForm.rows_count) === defaults.rows_count;
    if (!isDefaultLayout || playerPreset !== 'auto') return;
    const nextLayout = detectBestActorLayout(playerImageInfo, 'player');
    if (!nextLayout) return;
    setPlayerForm((current) => {
      if (Number(current.columns_count) === nextLayout.columns && Number(current.rows_count) === nextLayout.rows) {
        return current;
      }
      return {
        ...current,
        columns_count: nextLayout.columns,
        rows_count: nextLayout.rows
      };
    });
  }, [playerForm.sprite_image, playerForm.columns_count, playerForm.rows_count, playerImageInfo.height, playerImageInfo.width, playerPreset]);

  useEffect(() => {
    const width = Number(templateImageInfo.width) || 0;
    const height = Number(templateImageInfo.height) || 0;
    const columns = Math.max(1, Number(templateForm.columns_count) || 1);
    const rows = Math.max(1, Number(templateForm.rows_count) || 1);
    if (!width || !height || !columns || !rows) return;

    const nextFrameWidth = Math.max(1, Math.floor(width / columns));
    const nextFrameHeight = Math.max(1, Math.floor(height / rows));

    setTemplateForm((current) => {
      const currentFrameWidth = Number(current.frame_width) || 0;
      const currentFrameHeight = Number(current.frame_height) || 0;
      if (currentFrameWidth === nextFrameWidth && currentFrameHeight === nextFrameHeight) {
        return current;
      }
      return {
        ...current,
        frame_width: nextFrameWidth,
        frame_height: nextFrameHeight
      };
    });
  }, [templateForm.columns_count, templateForm.rows_count, templateImageInfo.height, templateImageInfo.width]);

  useEffect(() => {
    const width = Number(playerImageInfo.width) || 0;
    const height = Number(playerImageInfo.height) || 0;
    const columns = Math.max(1, Number(playerForm.columns_count) || 1);
    const rows = Math.max(1, Number(playerForm.rows_count) || 1);
    if (!width || !height || !columns || !rows) return;

    const nextFrameWidth = Math.max(1, Math.floor(width / columns));
    const nextFrameHeight = Math.max(1, Math.floor(height / rows));

    setPlayerForm((current) => {
      const currentFrameWidth = Number(current.frame_width) || 0;
      const currentFrameHeight = Number(current.frame_height) || 0;
      if (currentFrameWidth === nextFrameWidth && currentFrameHeight === nextFrameHeight) {
        return current;
      }
      return {
        ...current,
        frame_width: nextFrameWidth,
        frame_height: nextFrameHeight
      };
    });
  }, [playerForm.columns_count, playerForm.rows_count, playerImageInfo.height, playerImageInfo.width]);

  const applyTemplateAutoLayout = () => {
    if (!templateLayoutGuess) return;
    setTemplatePreset('auto');
    setTemplateForm((current) => ({
      ...current,
      columns_count: templateLayoutGuess.columns,
      rows_count: templateLayoutGuess.rows
    }));
  };

  const applyTemplatePreset = (presetKey) => {
    const preset = CHARSET_PRESETS[presetKey];
    if (!preset) return;
    setTemplatePreset(presetKey);
    if (preset.mode === 'auto') {
      applyTemplateAutoLayout();
      return;
    }
    setTemplateForm((current) => ({
      ...current,
      columns_count: preset.columns,
      rows_count: preset.rows
    }));
  };

  const applyPlayerAutoLayout = () => {
    if (!playerLayoutGuess) return;
    setPlayerPreset('auto');
    setPlayerForm((current) => ({
      ...current,
      columns_count: playerLayoutGuess.columns,
      rows_count: playerLayoutGuess.rows
    }));
  };

  const applyPlayerPreset = (presetKey) => {
    const preset = CHARSET_PRESETS[presetKey];
    if (!preset) return;
    setPlayerPreset(presetKey);
    if (preset.mode === 'auto') {
      applyPlayerAutoLayout();
      return;
    }
    setPlayerForm((current) => ({
      ...current,
      columns_count: preset.columns,
      rows_count: preset.rows
    }));
  };

  const selectedCity = useMemo(() => {
    if (!selectedCityId) return sortedCities[0] || null;
    return sortedCities.find((city) => Number(city.id) === Number(selectedCityId)) || sortedCities[0] || null;
  }, [selectedCityId, sortedCities]);

  const cityInstances = useMemo(() => {
    if (!selectedCity) return [];
    return (Array.isArray(instances) ? instances : []).filter((npc) => Number(npc.city_id) === Number(selectedCity.id));
  }, [instances, selectedCity]);

  const templateDirectionMap = useMemo(
    () => parseObjectJson(templateForm.direction_map, { south: 0, west: 1, east: 2, north: 3 }),
    [templateForm.direction_map]
  );

  const templateAnimationMap = useMemo(
    () => parseObjectJson(templateForm.animation_map, { idle: [1], walk: [0, 1, 2, 1] }),
    [templateForm.animation_map]
  );

  const templatePreviewState = useMemo(() => {
    const frameWidth = Math.max(8, Number(templateForm.frame_width) || 32);
    const frameHeight = Math.max(8, Number(templateForm.frame_height) || 32);
    const columns = Math.max(1, Number(templateForm.columns_count) || 4);
    const rows = Math.max(1, Number(templateForm.rows_count) || 4);
    const frames = getPreviewFrames(templateAnimationMap, templatePreviewBehavior);
    const frameIndex = Number(frames[templatePreviewTick % frames.length] ?? frames[0] ?? 0) || 0;
    const row = Number(templateDirectionMap?.[templatePreviewFacing] ?? templateDirectionMap?.south ?? 0) || 0;
    const useRowBasedSheet = frameIndex < columns;
    const sourceX = useRowBasedSheet ? frameIndex * frameWidth : (frameIndex % columns) * frameWidth;
    const sourceY = useRowBasedSheet ? row * frameHeight : Math.floor(frameIndex / columns) * frameHeight;
      return {
        frameWidth,
        frameHeight,
        columns,
      rows,
      row,
      frameIndex,
      sourceX,
      sourceY,
      stageWidth: Math.round(frameWidth * templatePreviewScale),
      stageHeight: Math.round(frameHeight * templatePreviewScale)
    };
  }, [templateAnimationMap, templateDirectionMap, templateForm.columns_count, templateForm.frame_height, templateForm.frame_width, templateForm.rows_count, templatePreviewBehavior, templatePreviewFacing, templatePreviewScale, templatePreviewTick]);

  const playerDirectionMap = useMemo(
    () => parseObjectJson(playerForm.direction_map, { south: 0, west: 1, east: 2, north: 3 }),
    [playerForm.direction_map]
  );

  const playerAnimationMap = useMemo(
    () => parseObjectJson(playerForm.animation_map, { idle: [1], walk: [0, 1, 2, 1] }),
    [playerForm.animation_map]
  );

  const playerPreviewState = useMemo(() => {
    const frameWidth = Math.max(8, Number(playerForm.frame_width) || 32);
    const frameHeight = Math.max(8, Number(playerForm.frame_height) || 48);
    const columns = Math.max(1, Number(playerForm.columns_count) || 3);
    const rows = Math.max(1, Number(playerForm.rows_count) || 4);
    const frames = getPreviewFrames(playerAnimationMap, playerPreviewBehavior);
    const frameIndex = Number(frames[playerPreviewTick % frames.length] ?? frames[0] ?? 0) || 0;
    const row = Number(playerDirectionMap?.[playerPreviewFacing] ?? playerDirectionMap?.south ?? 0) || 0;
    const useRowBasedSheet = frameIndex < columns;
    const sourceX = useRowBasedSheet ? frameIndex * frameWidth : (frameIndex % columns) * frameWidth;
    const sourceY = useRowBasedSheet ? row * frameHeight : Math.floor(frameIndex / columns) * frameHeight;
    return {
      frameWidth,
      frameHeight,
      columns,
      rows,
      row,
        frameIndex,
        sourceX,
        sourceY,
        stageWidth: Math.round(frameWidth * playerScaleValue),
        stageHeight: Math.round(frameHeight * playerScaleValue)
      };
    }, [playerAnimationMap, playerDirectionMap, playerForm.columns_count, playerForm.frame_height, playerForm.frame_width, playerForm.rows_count, playerPreviewBehavior, playerPreviewFacing, playerPreviewTick, playerScaleValue]);

  const editTemplate = (template) => {
    setTemplateForm({
      id: template.id,
      slug: template.slug || '',
      name: template.name || '',
      category: template.category || 'civilian',
      sprite_image: template.sprite_image || '',
      frame_width: Number(template.frame_width) || 32,
      frame_height: Number(template.frame_height) || 32,
      columns_count: Number(template.columns_count) || 4,
      rows_count: Number(template.rows_count) || 4,
      direction_map: JSON.stringify(template.direction_map || {}, null, 2),
      animation_map: JSON.stringify(template.animation_map || {}, null, 2),
      personality: template.personality || '',
      ai_enabled: !!template.ai_enabled,
      scale: Math.max(0.5, Number(template.metadata?.scale) || defaultNpcScale),
      use_player_scale: template.metadata?.use_player_scale !== false,
      metadata: JSON.stringify(template.metadata || {}, null, 2),
      show_foot_shadow: template.show_foot_shadow !== false && template.metadata?.show_foot_shadow !== false,
      is_active: !!template.is_active
    });
    setActiveSection('templates');
  };

  const editInstance = (npc) => {
    const nextCityId = String(npc.city_id || '');
    setSelectedCityId(nextCityId);
    setInstanceForm({
      id: npc.id,
      city_id: nextCityId,
      template_id: npc.template_id ? String(npc.template_id) : '',
      name: npc.name || '',
      x: Number(npc.x) || 0,
      y: Number(npc.y) || 0,
      layer: Number(npc.layer) || 1,
      facing: npc.facing || 'south',
      behavior: npc.behavior || 'idle',
      dialog_text: npc.dialog_text || '',
      target: npc.target || '',
      is_ai_enabled: !!npc.is_ai_enabled,
      is_enabled: !!npc.is_enabled,
      metadata: JSON.stringify(npc.metadata || {}, null, 2)
    });
    setActiveSection('placements');
  };

  const submitTemplate = (e) => {
    e.preventDefault();
    onSaveTemplate(normalizeTemplateForm(templateForm));
    setTemplateForm({
      ...emptyTemplateForm(),
      scale: defaultNpcScale,
      use_player_scale: true
    });
  };

  const submitInstance = (e) => {
    e.preventDefault();
    onSaveInstance(normalizeInstanceForm(instanceForm));
    setInstanceForm(emptyInstanceForm(selectedCity?.id || ''));
  };

  const submitPlayerAppearance = (e) => {
    e.preventDefault();
    onSavePlayerAppearance?.(normalizePlayerAppearanceForm(playerForm));
  };

  return (
    <div className="space-y-6">
      <section className="bg-[#11132d] p-6 rounded-3xl border border-slate-800 shadow-xl space-y-5">
        <div className="flex items-start justify-between gap-4 border-b border-slate-800 pb-4">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-emerald-300">
              <Bot size={12} /> NPCs y personajes
            </div>
            <h2 className="text-2xl font-bold text-white">Plantillas, sprites y colocación</h2>
            <p className="text-sm text-slate-400 max-w-2xl">
              Crea personajes con sprite sheet, define su animación y colócalos dentro de una ciudad para que luego puedan hablar, caminar o recibir IA.
            </p>
          </div>
          <div className="flex gap-2 rounded-2xl border border-slate-800 bg-slate-950/60 p-1">
            <button
              type="button"
              onClick={() => setActiveSection('templates')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${activeSection === 'templates' ? 'bg-game-primary text-white' : 'text-slate-400 hover:text-white'}`}
            >
              Plantillas
            </button>
            <button
              type="button"
              onClick={() => setActiveSection('placements')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${activeSection === 'placements' ? 'bg-game-primary text-white' : 'text-slate-400 hover:text-white'}`}
            >
              Colocación
            </button>
            <button
              type="button"
              onClick={() => setActiveSection('player')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${activeSection === 'player' ? 'bg-game-primary text-white' : 'text-slate-400 hover:text-white'}`}
            >
              Jugador
            </button>
          </div>
        </div>
      </section>

      {activeSection === 'templates' && (
        <div className="grid grid-cols-1 xl:grid-cols-[1.15fr_0.85fr] gap-6">
          <section className="bg-[#11132d] rounded-3xl border border-slate-800 shadow-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-900/30">
              <div>
                <h3 className="font-bold text-white">Plantillas NPC</h3>
                <p className="text-xs text-slate-500">Usa una plantilla como base para varios NPCs del mapa</p>
              </div>
            </div>
            <div className="divide-y divide-slate-800/70">
              {sortedTemplates.length === 0 ? (
                <div className="p-8 text-sm text-slate-500">Todavía no hay plantillas NPC creadas.</div>
              ) : sortedTemplates.map((template) => (
                <div key={template.id} className="p-4 flex items-center justify-between gap-4 hover:bg-white/5 transition-colors">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-14 h-14 rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden flex items-center justify-center shrink-0">
                      {template.sprite_image ? <img src={resolveImageSrc(template.sprite_image)} alt={template.name} className="w-full h-full object-cover" /> : <Bot size={20} className="text-slate-500" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="font-bold text-white truncate">{template.name}</h4>
                        <span className="rounded-full border border-slate-700 bg-slate-900 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-300">{template.category}</span>
                        {template.ai_enabled && (
                          <span className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-cyan-300">IA</span>
                        )}
                        {!template.is_active && (
                          <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-300">Inactivo</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate max-w-xl">
                        Slug: {template.slug} · Frame: {template.frame_width}x{template.frame_height} · Columnas: {template.columns_count} · Filas: {template.rows_count}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => editTemplate(template)}
                      className="rounded-lg bg-slate-800 p-2 text-slate-400 hover:text-white"
                    >
                      <Edit2 size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDeleteTemplate(template.id)}
                      className="rounded-lg bg-slate-800 p-2 text-slate-400 hover:text-rose-400"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-[#11132d] rounded-3xl border border-slate-800 shadow-xl p-6 space-y-5">
            <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
              <Bot size={22} className="text-game-primary" />
              <div>
                <h3 className="font-bold text-white">{templateForm.id ? 'Editar plantilla NPC' : 'Crear plantilla NPC'}</h3>
                <p className="text-xs text-slate-500">Sube el sprite sheet y define cómo se anima el personaje.</p>
              </div>
            </div>
            <form onSubmit={submitTemplate} className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Nombre</label>
                <input value={templateForm.name} onChange={(e) => setTemplateForm((current) => ({ ...current, name: e.target.value }))} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Slug</label>
                  <input value={templateForm.slug} onChange={(e) => setTemplateForm((current) => ({ ...current, slug: e.target.value }))} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Categoria</label>
                  <input value={templateForm.category} onChange={(e) => setTemplateForm((current) => ({ ...current, category: e.target.value }))} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" placeholder="civilian, merchant, guard..." />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Sprite sheet</label>
                <div className="flex gap-3">
                  <input value={templateForm.sprite_image} onChange={(e) => setTemplateForm((current) => ({ ...current, sprite_image: e.target.value }))} className="flex-1 rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" placeholder="npc-hero.png" />
                  <button
                    type="button"
                    onClick={() => onOpenImageGallery?.('npc-template')}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/60 px-4 py-3 text-sm font-bold text-white hover:border-game-primary hover:bg-slate-800"
                  >
                    <ImageIcon size={16} /> Galeria
                  </button>
                </div>
                <div className="text-[11px] text-slate-500">
                  {templateImageInfo.width > 0 && templateImageInfo.height > 0
                    ? `Imagen detectada: ${templateImageInfo.width}x${templateImageInfo.height}px. El frame se calcula automáticamente según columnas y filas.`
                    : 'Cuando elijas una imagen, el ancho y alto del frame se calcularán automáticamente.'}
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Preset de charset</label>
                  <select
                    value={templatePreset}
                    onChange={(e) => applyTemplatePreset(e.target.value)}
                    className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                  >
                    <option value="auto">Auto</option>
                    <option value="rpg_maker">RPG Maker clásico</option>
                    <option value="classic_3x4">3x4 estándar</option>
                    <option value="npc_4x4">4x4 NPC</option>
                    <option value="free">Sheet libre</option>
                  </select>
                </div>
                {templateLayoutGuess && templateImageInfo.width > 0 && templateImageInfo.height > 0 && (
                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-cyan-300">
                    <span className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2.5 py-1 font-semibold uppercase tracking-widest">
                      Auto sugerido
                    </span>
                    <span>{templateLayoutGuess.columns} columnas</span>
                    <span>·</span>
                    <span>{templateLayoutGuess.rows} filas</span>
                    <span>·</span>
                    <span>Frame {templateLayoutGuess.frameWidth}x{templateLayoutGuess.frameHeight}</span>
                  </div>
                )}
              </div>
              <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h4 className="font-bold text-white">Previsualizador NPC</h4>
                    <p className="text-xs text-slate-500">Comprueba movimiento, dirección, escala y el frame exacto que se está usando.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setTemplatePreviewAutoplay((current) => !current)}
                    className="rounded-xl border border-slate-700 bg-slate-900/60 px-3 py-2 text-xs font-bold text-white hover:border-game-primary"
                  >
                    {templatePreviewAutoplay ? 'Pausar animación' : 'Reanudar animación'}
                  </button>
                </div>
                <div className="grid grid-cols-1 xl:grid-cols-[0.85fr_1.15fr] gap-4">
                  <div className="space-y-4">
                    <div className="rounded-2xl border border-slate-800 bg-[radial-gradient(circle_at_center,rgba(14,165,233,0.08),transparent_60%),linear-gradient(180deg,rgba(15,23,42,0.85),rgba(2,6,23,0.96))] p-4">
                      <div className="flex h-[240px] items-end justify-center overflow-hidden rounded-2xl border border-slate-800 bg-[linear-gradient(rgba(148,163,184,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.06)_1px,transparent_1px)] bg-[size:32px_32px]">
                        {templateForm.sprite_image ? (
                          <div
                            className="shrink-0 bg-no-repeat pixelated"
                            style={{
                              width: `${templatePreviewState.stageWidth}px`,
                              height: `${templatePreviewState.stageHeight}px`,
                              imageRendering: 'pixelated',
                              backgroundImage: `url(${resolveImageSrc(templateForm.sprite_image)})`,
                              backgroundPosition: `-${templatePreviewState.sourceX * templatePreviewScale}px -${templatePreviewState.sourceY * templatePreviewScale}px`,
                              backgroundSize: `${templatePreviewState.columns * templatePreviewState.frameWidth * templatePreviewScale}px ${templatePreviewState.rows * templatePreviewState.frameHeight * templatePreviewScale}px`
                            }}
                          />
                        ) : (
                          <div className="text-sm text-slate-500">Selecciona un sprite sheet para previsualizar el NPC.</div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Dirección</label>
                        <select value={templatePreviewFacing} onChange={(e) => setTemplatePreviewFacing(e.target.value)} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none">
                          <option value="south">Sur</option>
                          <option value="west">Oeste</option>
                          <option value="east">Este</option>
                          <option value="north">Norte</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Animación</label>
                        <select value={templatePreviewBehavior} onChange={(e) => setTemplatePreviewBehavior(e.target.value)} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none">
                          {Object.keys(templateAnimationMap).length > 0 ? Object.keys(templateAnimationMap).map((key) => (
                            <option key={key} value={key}>{key}</option>
                          )) : (
                            <>
                              <option value="idle">idle</option>
                              <option value="walk">walk</option>
                            </>
                          )}
                        </select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs font-bold uppercase tracking-widest text-slate-400">
                        <span>Escala de vista</span>
                        <span>{templatePreviewScale.toFixed(2)}x</span>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="4"
                        step="0.1"
                        value={templatePreviewScale}
                        onChange={(e) => setTemplatePreviewScale(Number(e.target.value) || 2.2)}
                        className="w-full accent-cyan-500"
                      />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
                      <div className="overflow-auto rounded-2xl border border-slate-800 bg-slate-950 p-3">
                        {templateForm.sprite_image ? (
                          <div className="relative inline-block">
                            <img
                              src={resolveImageSrc(templateForm.sprite_image)}
                              alt={templateForm.name || 'NPC preview'}
                              className="max-h-[320px] object-contain pixelated"
                              style={{ imageRendering: 'pixelated' }}
                            />
                            <div
                              className="absolute border-2 border-cyan-400 bg-cyan-400/20 shadow-[0_0_0_1px_rgba(255,255,255,0.15)]"
                              style={{
                                left: `${templatePreviewState.sourceX}px`,
                                top: `${templatePreviewState.sourceY}px`,
                                width: `${templatePreviewState.frameWidth}px`,
                                height: `${templatePreviewState.frameHeight}px`
                              }}
                            />
                          </div>
                        ) : (
                          <div className="flex h-48 items-center justify-center text-sm text-slate-500">La hoja del NPC aparecerá aquí con el frame resaltado.</div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs text-slate-400">
                      <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-3">
                        <div className="uppercase tracking-widest text-slate-500">Frame actual</div>
                        <div className="mt-1 font-bold text-white">#{templatePreviewState.frameIndex}</div>
                      </div>
                      <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-3">
                        <div className="uppercase tracking-widest text-slate-500">Fila actual</div>
                        <div className="mt-1 font-bold text-white">#{templatePreviewState.row}</div>
                      </div>
                      <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-3">
                        <div className="uppercase tracking-widest text-slate-500">Recorte X,Y</div>
                        <div className="mt-1 font-bold text-white">{templatePreviewState.sourceX},{templatePreviewState.sourceY}</div>
                      </div>
                      <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-3">
                        <div className="uppercase tracking-widest text-slate-500">Tamaño frame</div>
                        <div className="mt-1 font-bold text-white">{templatePreviewState.frameWidth}x{templatePreviewState.frameHeight}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Ancho frame</label>
                  <input type="number" value={templateForm.frame_width} readOnly className="w-full rounded-xl border border-slate-800 bg-slate-950/70 p-3 text-white/90 focus:outline-none cursor-default" />
                  <p className="text-[11px] text-slate-500">Automático</p>
                  </div>
                  <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Alto frame</label>
                  <input type="number" value={templateForm.frame_height} readOnly className="w-full rounded-xl border border-slate-800 bg-slate-950/70 p-3 text-white/90 focus:outline-none cursor-default" />
                  <p className="text-[11px] text-slate-500">Automático</p>
                  </div>
                  <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Columnas</label>
                  <input type="number" value={templateForm.columns_count} onChange={(e) => setTemplateForm((current) => ({ ...current, columns_count: Number(e.target.value) || 4 }))} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Filas</label>
                  <input type="number" value={templateForm.rows_count} onChange={(e) => setTemplateForm((current) => ({ ...current, rows_count: Number(e.target.value) || 4 }))} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" />
                </div>
                <div className="space-y-2 md:col-span-4">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Auto completo</label>
                  <button
                    type="button"
                    onClick={applyTemplateAutoLayout}
                    disabled={!templateLayoutGuess}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-4 py-3 font-bold text-cyan-200 hover:border-cyan-400 hover:bg-cyan-500/20 disabled:opacity-40"
                  >
                    Auto detectar layout
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Direction map JSON</label>
                <textarea value={templateForm.direction_map} onChange={(e) => setTemplateForm((current) => ({ ...current, direction_map: e.target.value }))} className="h-24 w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none font-mono text-xs" />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Animation map JSON</label>
                <textarea value={templateForm.animation_map} onChange={(e) => setTemplateForm((current) => ({ ...current, animation_map: e.target.value }))} className="h-24 w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none font-mono text-xs" />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Escala en la Cúpula</label>
                <div className="rounded-2xl border border-slate-700 bg-slate-900/50 p-4 space-y-3">
                  <label className="flex items-center justify-between text-sm text-slate-300">
                    <span>Usar la misma escala que el jugador</span>
                    <button
                      type="button"
                      onClick={() => setTemplateForm((current) => ({
                        ...current,
                        use_player_scale: !current.use_player_scale,
                        scale: current.use_player_scale ? defaultNpcScale : current.scale
                      }))}
                      className="text-cyan-300"
                    >
                      {templateForm.use_player_scale !== false ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                    </button>
                  </label>
                  <p className="text-xs text-slate-500">
                    {templateForm.use_player_scale !== false
                      ? `Este NPC heredará la escala del jugador (${defaultNpcScale.toFixed(2)}x).`
                      : 'Este NPC usará una escala propia en la Cúpula.'}
                  </p>
                  {templateForm.use_player_scale === false && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs font-bold uppercase tracking-widest text-slate-400">
                        <span>Escala propia</span>
                        <span>{Math.max(0.5, Number(templateForm.scale) || defaultNpcScale).toFixed(2)}x</span>
                      </div>
                      <input
                        type="range"
                        min="0.5"
                        max="4"
                        step="0.1"
                        value={Math.max(0.5, Number(templateForm.scale) || defaultNpcScale)}
                        onChange={(e) => setTemplateForm((current) => ({ ...current, scale: Number(e.target.value) || defaultNpcScale }))}
                        className="w-full accent-cyan-500"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Personalidad</label>
                <textarea value={templateForm.personality} onChange={(e) => setTemplateForm((current) => ({ ...current, personality: e.target.value }))} className="h-24 w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" placeholder="Amable, serio, curioso..." />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Metadata JSON</label>
                <textarea value={templateForm.metadata} onChange={(e) => setTemplateForm((current) => ({ ...current, metadata: e.target.value }))} className="h-24 w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none font-mono text-xs" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="flex items-center justify-between rounded-xl border border-slate-700 bg-slate-900/50 px-4 py-3 text-sm text-slate-300">
                  <span>IA activa</span>
                  <button type="button" onClick={() => setTemplateForm((current) => ({ ...current, ai_enabled: !current.ai_enabled }))} className="text-cyan-300">
                    {templateForm.ai_enabled ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                  </button>
                </label>
                <label className="flex items-center justify-between rounded-xl border border-slate-700 bg-slate-900/50 px-4 py-3 text-sm text-slate-300">
                  <span>Activo</span>
                  <button type="button" onClick={() => setTemplateForm((current) => ({ ...current, is_active: !current.is_active }))} className="text-cyan-300">
                    {templateForm.is_active ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                  </button>
                </label>
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={isSavingTemplate} className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-game-primary px-4 py-3 font-bold text-white hover:bg-game-primary/80 disabled:opacity-50">
                  <Save size={16} /> {isSavingTemplate ? 'Guardando...' : (templateForm.id ? 'Actualizar plantilla' : 'Crear plantilla')}
                </button>
                <button
                  type="button"
                  onClick={() => setTemplateForm({
                    ...emptyTemplateForm(),
                    scale: defaultNpcScale,
                    use_player_scale: true
                  })}
                  className="rounded-xl border border-slate-700 px-4 py-3 font-bold text-slate-300 hover:border-slate-500 hover:text-white"
                >
                  Limpiar
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {activeSection === 'placements' && (
        <div className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-6">
          <section className="bg-[#11132d] rounded-3xl border border-slate-800 shadow-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-900/30">
              <div>
                <h3 className="font-bold text-white">NPCs colocados</h3>
                <p className="text-xs text-slate-500">Elige una ciudad y pon los NPCs dentro del mapa</p>
              </div>
              <div className="min-w-[220px]">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Ciudad</label>
                <select
                  value={selectedCityId}
                  onChange={(e) => {
                    setSelectedCityId(e.target.value);
                    setInstanceForm((current) => ({ ...current, city_id: e.target.value }));
                  }}
                  className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                >
                  <option value="">Selecciona una ciudad</option>
                  {sortedCities.map((city) => (
                    <option key={city.id} value={city.id}>{city.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="divide-y divide-slate-800/70">
              {cityInstances.length === 0 ? (
                <div className="p-8 text-sm text-slate-500">Aun no hay NPCs colocados en esta ciudad.</div>
              ) : cityInstances.map((npc) => (
                <div key={npc.id} className="p-4 flex items-center justify-between gap-4 hover:bg-white/5 transition-colors">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-14 h-14 rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden flex items-center justify-center shrink-0">
                      {npc.template?.sprite_image ? <img src={resolveImageSrc(npc.template.sprite_image)} alt={npc.name} className="w-full h-full object-cover" /> : <MapPinned size={20} className="text-slate-500" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="font-bold text-white truncate">{npc.name}</h4>
                        <span className="rounded-full border border-slate-700 bg-slate-900 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-300">
                          {npc.template?.name || 'Sin plantilla'}
                        </span>
                        {npc.is_ai_enabled && (
                          <span className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-cyan-300">IA</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate max-w-xl">
                        Pos: {npc.x},{npc.y} · Capa: {npc.layer} · Mira: {npc.facing} · Estado: {npc.behavior}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => editInstance(npc)}
                      className="rounded-lg bg-slate-800 p-2 text-slate-400 hover:text-white"
                    >
                      <Edit2 size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDeleteInstance(npc.id)}
                      className="rounded-lg bg-slate-800 p-2 text-slate-400 hover:text-rose-400"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-[#11132d] rounded-3xl border border-slate-800 shadow-xl p-6 space-y-5">
            <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
              <MapPinned size={22} className="text-game-primary" />
              <div>
                <h3 className="font-bold text-white">{instanceForm.id ? 'Editar NPC' : 'Colocar NPC en ciudad'}</h3>
                <p className="text-xs text-slate-500">Usa una plantilla y define su posición dentro de la Cúpula.</p>
              </div>
            </div>
            <form onSubmit={submitInstance} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Ciudad</label>
                  <select
                    value={instanceForm.city_id}
                    onChange={(e) => setInstanceForm((current) => ({ ...current, city_id: e.target.value }))}
                    className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                  >
                    <option value="">Selecciona una ciudad</option>
                    {sortedCities.map((city) => (
                      <option key={city.id} value={city.id}>{city.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Plantilla</label>
                  <select
                    value={instanceForm.template_id}
                    onChange={(e) => setInstanceForm((current) => ({ ...current, template_id: e.target.value }))}
                    className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                  >
                    <option value="">Sin plantilla</option>
                    {sortedTemplates.map((template) => (
                      <option key={template.id} value={template.id}>{template.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Nombre</label>
                <input value={instanceForm.name} onChange={(e) => setInstanceForm((current) => ({ ...current, name: e.target.value }))} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" placeholder="Tendero, guardia, aldeano..." />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">X</label>
                  <input type="number" value={instanceForm.x} onChange={(e) => setInstanceForm((current) => ({ ...current, x: Number(e.target.value) || 0 }))} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Y</label>
                  <input type="number" value={instanceForm.y} onChange={(e) => setInstanceForm((current) => ({ ...current, y: Number(e.target.value) || 0 }))} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Capa</label>
                  <input type="number" value={instanceForm.layer} onChange={(e) => setInstanceForm((current) => ({ ...current, layer: Number(e.target.value) || 1 }))} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Mirada</label>
                  <select value={instanceForm.facing} onChange={(e) => setInstanceForm((current) => ({ ...current, facing: e.target.value }))} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none">
                    <option value="south">Sur</option>
                    <option value="north">Norte</option>
                    <option value="east">Este</option>
                    <option value="west">Oeste</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Comportamiento</label>
                <input value={instanceForm.behavior} onChange={(e) => setInstanceForm((current) => ({ ...current, behavior: e.target.value }))} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" placeholder="idle, merchant, guard, patrol..." />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Dialogo</label>
                <textarea value={instanceForm.dialog_text} onChange={(e) => setInstanceForm((current) => ({ ...current, dialog_text: e.target.value }))} className="h-24 w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" placeholder="Texto que verá el jugador..." />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Destino / accion</label>
                <input value={instanceForm.target} onChange={(e) => setInstanceForm((current) => ({ ...current, target: e.target.value }))} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" placeholder="/city/shop, interior:tienda..." />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Metadata JSON</label>
                <textarea value={instanceForm.metadata} onChange={(e) => setInstanceForm((current) => ({ ...current, metadata: e.target.value }))} className="h-24 w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none font-mono text-xs" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="flex items-center justify-between rounded-xl border border-slate-700 bg-slate-900/50 px-4 py-3 text-sm text-slate-300">
                  <span>IA activa</span>
                  <button type="button" onClick={() => setInstanceForm((current) => ({ ...current, is_ai_enabled: !current.is_ai_enabled }))} className="text-cyan-300">
                    {instanceForm.is_ai_enabled ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                  </button>
                </label>
                <label className="flex items-center justify-between rounded-xl border border-slate-700 bg-slate-900/50 px-4 py-3 text-sm text-slate-300">
                  <span>Visible</span>
                  <button type="button" onClick={() => setInstanceForm((current) => ({ ...current, is_enabled: !current.is_enabled }))} className="text-cyan-300">
                    {instanceForm.is_enabled ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                  </button>
                </label>
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={isSavingInstance} className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-game-primary px-4 py-3 font-bold text-white hover:bg-game-primary/80 disabled:opacity-50">
                  <Save size={16} /> {isSavingInstance ? 'Guardando...' : (instanceForm.id ? 'Actualizar NPC' : 'Colocar NPC')}
                </button>
                <button type="button" onClick={() => setInstanceForm(emptyInstanceForm(selectedCity?.id || ''))} className="rounded-xl border border-slate-700 px-4 py-3 font-bold text-slate-300 hover:border-slate-500 hover:text-white">
                  Limpiar
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {activeSection === 'player' && (
        <div className="grid grid-cols-1 xl:grid-cols-[0.95fr_1.05fr] gap-6">
          <section className="bg-[#11132d] rounded-3xl border border-slate-800 shadow-xl p-6 space-y-5">
            <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
              <Bot size={22} className="text-game-primary" />
              <div>
                <h3 className="font-bold text-white">Personaje compartido</h3>
                <p className="text-xs text-slate-500">Configura el charset compartido para todos los jugadores en la Cúpula y los interiores.</p>
              </div>
            </div>
            <form onSubmit={submitPlayerAppearance} className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Charset / sprite sheet</label>
                <div className="flex gap-3">
                  <input value={playerForm.sprite_image} onChange={(e) => setPlayerForm((current) => ({ ...current, sprite_image: e.target.value }))} className="flex-1 rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" placeholder="player-rpg.png" />
                  <button
                    type="button"
                    onClick={() => onOpenImageGallery?.('player-appearance')}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/60 px-4 py-3 text-sm font-bold text-white hover:border-game-primary hover:bg-slate-800"
                  >
                    <ImageIcon size={16} /> Galeria
                  </button>
                </div>
                <div className="text-[11px] text-slate-500">
                  {playerImageInfo.width > 0 && playerImageInfo.height > 0
                    ? `Imagen detectada: ${playerImageInfo.width}x${playerImageInfo.height}px. El frame se calcula automáticamente según columnas y filas.`
                    : 'Cuando elijas una imagen, el ancho y alto del frame se calcularán automáticamente.'}
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Preset de charset</label>
                  <select
                    value={playerPreset}
                    onChange={(e) => applyPlayerPreset(e.target.value)}
                    className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                  >
                    <option value="auto">Auto</option>
                    <option value="rpg_maker">RPG Maker clásico</option>
                    <option value="classic_3x4">3x4 estándar</option>
                    <option value="free">Sheet libre</option>
                  </select>
                </div>
                {playerLayoutGuess && playerImageInfo.width > 0 && playerImageInfo.height > 0 && (
                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-cyan-300">
                    <span className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2.5 py-1 font-semibold uppercase tracking-widest">
                      Auto sugerido
                    </span>
                    <span>{playerLayoutGuess.columns} columnas</span>
                    <span>·</span>
                    <span>{playerLayoutGuess.rows} filas</span>
                    <span>·</span>
                    <span>Frame {playerLayoutGuess.frameWidth}x{playerLayoutGuess.frameHeight}</span>
                  </div>
                )}
              </div>
              <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h4 className="font-bold text-white">Previsualizador del jugador</h4>
                    <p className="text-xs text-slate-500">Comprueba movimiento, dirección, escala y el frame exacto que usará el personaje.</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPlayerForm((current) => ({ ...current, show_foot_shadow: !current.show_foot_shadow }))}
                      className={`rounded-xl border px-3 py-2 text-xs font-bold transition-colors ${playerForm.show_foot_shadow ? 'border-cyan-500/30 bg-cyan-500/10 text-cyan-200 hover:border-cyan-400' : 'border-slate-700 bg-slate-900/60 text-slate-300 hover:border-game-primary'}`}
                    >
                      Sombra pies: {playerForm.show_foot_shadow ? 'Sí' : 'No'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPlayerPreviewAutoplay((current) => !current)}
                      className="rounded-xl border border-slate-700 bg-slate-900/60 px-3 py-2 text-xs font-bold text-white hover:border-game-primary"
                    >
                      {playerPreviewAutoplay ? 'Pausar animación' : 'Reanudar animación'}
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 xl:grid-cols-[0.85fr_1.15fr] gap-4">
                  <div className="space-y-4">
                    <div className="rounded-2xl border border-slate-800 bg-[radial-gradient(circle_at_center,rgba(34,211,238,0.08),transparent_60%),linear-gradient(180deg,rgba(15,23,42,0.85),rgba(2,6,23,0.96))] p-4">
                      <div className="flex h-[260px] items-end justify-center overflow-hidden rounded-2xl border border-slate-800 bg-[linear-gradient(rgba(148,163,184,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.06)_1px,transparent_1px)] bg-[size:32px_32px]">
                        {playerForm.sprite_image ? (
                          <div
                            className="shrink-0 bg-no-repeat pixelated"
                            style={{
                              width: `${playerPreviewState.stageWidth}px`,
                              height: `${playerPreviewState.stageHeight}px`,
                              imageRendering: 'pixelated',
                              backgroundImage: `url(${resolveImageSrc(playerForm.sprite_image)})`,
                              backgroundPosition: `-${playerPreviewState.sourceX * playerScaleValue}px -${playerPreviewState.sourceY * playerScaleValue}px`,
                              backgroundSize: `${playerPreviewState.columns * playerPreviewState.frameWidth * playerScaleValue}px ${playerPreviewState.rows * playerPreviewState.frameHeight * playerScaleValue}px`
                            }}
                          />
                        ) : (
                          <div className="text-sm text-slate-500">Selecciona un charset para previsualizar al jugador.</div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Dirección</label>
                        <select value={playerPreviewFacing} onChange={(e) => setPlayerPreviewFacing(e.target.value)} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none">
                          <option value="south">Sur</option>
                          <option value="west">Oeste</option>
                          <option value="east">Este</option>
                          <option value="north">Norte</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Animación</label>
                        <select value={playerPreviewBehavior} onChange={(e) => setPlayerPreviewBehavior(e.target.value)} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none">
                          {Object.keys(playerAnimationMap).length > 0 ? Object.keys(playerAnimationMap).map((key) => (
                            <option key={key} value={key}>{key}</option>
                          )) : (
                            <>
                              <option value="idle">idle</option>
                              <option value="walk">walk</option>
                            </>
                          )}
                        </select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs font-bold uppercase tracking-widest text-slate-400">
                        <span>Escala de vista</span>
                        <span>{playerScaleValue.toFixed(2)}x</span>
                      </div>
                      <input
                        type="range"
                        min="0.5"
                        max="4"
                        step="0.1"
                        value={playerScaleValue}
                        onChange={(e) => setPlayerForm((current) => ({ ...current, scale: Number(e.target.value) || 1.65 }))}
                        className="w-full accent-cyan-500"
                      />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
                      <div className="overflow-auto rounded-2xl border border-slate-800 bg-slate-950 p-3">
                        {playerForm.sprite_image ? (
                          <div className="relative inline-block">
                            <img
                              src={resolveImageSrc(playerForm.sprite_image)}
                              alt="Player preview"
                              className="max-h-[320px] object-contain pixelated"
                              style={{ imageRendering: 'pixelated' }}
                            />
                            <div
                              className="absolute border-2 border-cyan-400 bg-cyan-400/20 shadow-[0_0_0_1px_rgba(255,255,255,0.15)]"
                              style={{
                                left: `${playerPreviewState.sourceX}px`,
                                top: `${playerPreviewState.sourceY}px`,
                                width: `${playerPreviewState.frameWidth}px`,
                                height: `${playerPreviewState.frameHeight}px`
                              }}
                            />
                          </div>
                        ) : (
                          <div className="flex h-48 items-center justify-center text-sm text-slate-500">La hoja del jugador aparecerá aquí con el frame resaltado.</div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs text-slate-400">
                      <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-3">
                        <div className="uppercase tracking-widest text-slate-500">Frame actual</div>
                        <div className="mt-1 font-bold text-white">#{playerPreviewState.frameIndex}</div>
                      </div>
                      <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-3">
                        <div className="uppercase tracking-widest text-slate-500">Fila actual</div>
                        <div className="mt-1 font-bold text-white">#{playerPreviewState.row}</div>
                      </div>
                      <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-3">
                        <div className="uppercase tracking-widest text-slate-500">Recorte X,Y</div>
                        <div className="mt-1 font-bold text-white">{playerPreviewState.sourceX},{playerPreviewState.sourceY}</div>
                      </div>
                      <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-3">
                        <div className="uppercase tracking-widest text-slate-500">Tamaño frame</div>
                        <div className="mt-1 font-bold text-white">{playerPreviewState.frameWidth}x{playerPreviewState.frameHeight}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Ancho frame</label>
                  <input type="number" value={playerForm.frame_width} readOnly className="w-full rounded-xl border border-slate-800 bg-slate-950/70 p-3 text-white/90 focus:outline-none cursor-default" />
                  <p className="text-[11px] text-slate-500">Automático</p>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Alto frame</label>
                  <input type="number" value={playerForm.frame_height} readOnly className="w-full rounded-xl border border-slate-800 bg-slate-950/70 p-3 text-white/90 focus:outline-none cursor-default" />
                  <p className="text-[11px] text-slate-500">Automático</p>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Columnas</label>
                  <input type="number" value={playerForm.columns_count} onChange={(e) => setPlayerForm((current) => ({ ...current, columns_count: Number(e.target.value) || 3 }))} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Filas</label>
                  <input type="number" value={playerForm.rows_count} onChange={(e) => setPlayerForm((current) => ({ ...current, rows_count: Number(e.target.value) || 4 }))} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" />
                </div>
                <div className="space-y-2 md:col-span-4">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Auto completo</label>
                  <button
                    type="button"
                    onClick={applyPlayerAutoLayout}
                    disabled={!playerLayoutGuess}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-4 py-3 font-bold text-cyan-200 hover:border-cyan-400 hover:bg-cyan-500/20 disabled:opacity-40"
                  >
                    Auto detectar layout
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Direction map JSON</label>
                <textarea value={playerForm.direction_map} onChange={(e) => setPlayerForm((current) => ({ ...current, direction_map: e.target.value }))} className="h-24 w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none font-mono text-xs" />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Animation map JSON</label>
                <textarea value={playerForm.animation_map} onChange={(e) => setPlayerForm((current) => ({ ...current, animation_map: e.target.value }))} className="h-24 w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none font-mono text-xs" />
              </div>
              <div className="flex gap-3">
                  <button type="submit" disabled={isSavingPlayerAppearance} className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-game-primary px-4 py-3 font-bold text-white hover:bg-game-primary/80 disabled:opacity-50">
                  <Save size={16} /> {isSavingPlayerAppearance ? 'Guardando...' : 'Guardar personaje compartido'}
                </button>
                <button type="button" onClick={() => setPlayerForm(emptyPlayerAppearanceForm())} className="rounded-xl border border-slate-700 px-4 py-3 font-bold text-slate-300 hover:border-slate-500 hover:text-white">
                  Limpiar
                </button>
              </div>
            </form>
          </section>

          <section className="bg-[#11132d] rounded-3xl border border-slate-800 shadow-xl p-6 space-y-5">
            <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
              <ImageIcon size={22} className="text-game-primary" />
              <div>
                <h3 className="font-bold text-white">Vista previa rápida</h3>
                <p className="text-xs text-slate-500">Usa una hoja 3x4 o similar para un movimiento RPG clásico.</p>
              </div>
            </div>
            <div className="rounded-3xl border border-slate-800 bg-slate-950/60 p-6 flex items-center justify-center min-h-[340px]">
              {playerForm.sprite_image ? (
                <img src={resolveImageSrc(playerForm.sprite_image)} alt="Charset jugador" className="max-h-[280px] object-contain pixelated" />
              ) : (
                <div className="text-sm text-slate-500">Selecciona una imagen desde la galería para el personaje del usuario.</div>
              )}
            </div>
            <div className="text-xs text-slate-400 space-y-1">
              <p>Direcciones recomendadas por filas: `sur`, `izquierda`, `derecha`, `norte`.</p>
              <p>Animación recomendada para caminar: `0,1,2,1`.</p>
              <p>El motor ancla el personaje al suelo para evitar que quede cortado o flotando.</p>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export default forwardRef(RpgNpcManager);
